import { useEffect, useMemo } from "react";
import { useState } from "react";
import { debounce } from "lodash";
import actions from "store/actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { companiesListSelector } from "store/selectors";

import { showErrorModal } from "helpers";

import {
  queryUsersControllerGetUserCompanies,
  UsersControllerGetUserCompaniesQueryParams,
} from "utils/swagger_react_query";
import { ListUserCompaniesResDto } from "utils/swagger_react_query";

const defaultPayload: UsersControllerGetUserCompaniesQueryParams = {
  page: "1",
  perPage: "6",
  searchString: "",
};

export interface IUseCompaniesList {
  setIsPageLoading?: (state: boolean) => void;
  setIsListLoading?: (state: boolean) => void;
  fetchOnMount?: boolean;
  perPage?: string;
  initValueFromStore?: boolean;
}

export const useCompaniesList = ({
  setIsPageLoading,
  setIsListLoading,
  fetchOnMount,
  perPage,
  initValueFromStore,
}: IUseCompaniesList) => {
  const dispatch = useAppDispatch();
  const currentCompaniesList = useAppSelector(companiesListSelector);

  const [companiesList, setCompaniesList] = useState<ListUserCompaniesResDto>(
    initValueFromStore && currentCompaniesList
      ? currentCompaniesList
      : {
          companies: [],
          perPage: +(perPage || defaultPayload.perPage),
          total: 0,
          page: +defaultPayload.page,
        },
  );
  const [searchInputValue, setSearchInputValue] = useState("");

  useEffect(() => {
    if (fetchOnMount) {
      fetchCompanies({
        page: companiesList.page.toString(),
        perPage: companiesList.perPage.toString(),
        searchString: searchInputValue,
      });
    }

    return () => {
      debouncedFetchCompanies.cancel();
    };
  }, []);

  const fetchCompanies = async (
    payload: UsersControllerGetUserCompaniesQueryParams = {
      page: companiesList.page.toString(),
      perPage: companiesList.perPage.toString(),
    },
    saveToStore?: boolean,
  ) => {
    try {
      setIsListLoading?.(true);
      const companiesListRes = await queryUsersControllerGetUserCompanies(payload);
      setCompaniesList(companiesListRes);
      if (saveToStore) {
        saveCompaniesListToStore(companiesListRes);
      }
      return companiesListRes;
    } catch (error) {
      showErrorModal(error);
    } finally {
      setIsListLoading?.(false);
      setIsPageLoading?.(false);
    }
  };

  const debouncedFetchCompanies = useMemo(() => {
    return debounce(async (payload: UsersControllerGetUserCompaniesQueryParams) => {
      await fetchCompanies(payload);
    }, 500);
  }, []);

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    setCompaniesList({
      ...companiesList,
      page: 1,
    });
    debouncedFetchCompanies({
      page: companiesList.page.toString(),
      perPage: companiesList.perPage.toString(),
      searchString: value,
    });
  };

  const handlePageChange = (page: number) => {
    setCompaniesList({
      ...companiesList,
      page,
    });
    fetchCompanies({
      page: page.toString(),
      perPage: companiesList.perPage.toString(),
      searchString: searchInputValue,
    });
  };

  const saveCompaniesListToStore = (companiesList: ListUserCompaniesResDto) => {
    dispatch(actions.userMetaData.setCompaniesList(companiesList));
  };

  const resetData = (replaceFromStore: boolean = false) => {
    if (currentCompaniesList && replaceFromStore) {
      setCompaniesList(currentCompaniesList);
    } else {
      setCompaniesList({
        companies: [],
        perPage: +(perPage || defaultPayload.perPage),
        total: 0,
        page: +defaultPayload.page,
      });
    }
    setSearchInputValue("");
  };

  return {
    companiesList,
    searchInputValue,
    saveCompaniesListToStore,
    actions: {
      setSearchInputValue,
      refetchCompanies: fetchCompanies,
      refetchCompaniesDebounced: debouncedFetchCompanies,
      handleSearchInputChange,
      handlePageChange,
      resetData,
    },
  };
};
